import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Search, FilterList } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  setSearch,
  resetAll,
  setFilter,
} from "../../../../redux/stockHistory/stockHistorySlice";
import { useGetList } from "../../../../hooks/useGetList";
import { RotateCcw, XIcon } from "lucide-react";
import debounce from "../../../../Utils/debounce";

const logTypeOptions = [
  { value: "", label: "All Log Types" },
  { value: "initial", label: "Initial" },
  { value: "sale", label: "Sale" },
  { value: "unit-convert", label: "Unit Convert" },
  { value: "purchase", label: "Purchase" },
];

const StockHistoryHeader = () => {
  const dispatch = useDispatch();
  const filter = useSelector((state) => state.filterStockHistory);

  const { data: branchData, loading: branchLoading } =
    useGetList(`branch/options`);
  const {
    data: productItemData,
    loading: productItemLoading,
    fetchData: fetchProductItem,
  } = useGetList(
    `product-item/options`,
    filter.filterType?.relatedBranch ? true : false,
    {
      isPopulateUnits: "true",
      relatedBranch: filter.filterType?.relatedBranch,
    },
  );

  const inputRef = useRef();

  const [tempStartDate, setTempStartDate] = useState(
    filter?.filterType?.startDate || "",
  );
  const [tempEndDate, setTempEndDate] = useState(
    filter?.filterType?.endDate || "",
  );

  const handleSearchChange = debounce(() => {
    if (inputRef.current) {
      dispatch(setSearch(inputRef.current.value));
    }
  }, 500);

  const handleProductItemChange = (event, newValue) => {
    dispatch(
      setFilter({
        relatedItem: newValue ? newValue.value : "",
        relatedUnit: "",
      }),
    );
  };

  const handleBranchChange = (event) => {
    if (event.target.value === "All") {
      dispatch(setFilter({ relatedBranch: "All" }));
    } else {
      dispatch(setFilter({ relatedBranch: event.target.value }));
    }
  };

  const handleUnitChange = (event) => {
    dispatch(setFilter({ relatedUnit: event.target.value }));
  };

  const handleLogTypeChange = (event) => {
    dispatch(setFilter({ logType: event.target.value }));
  };

  const handleStartDateChange = (event) => {
    setTempStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setTempEndDate(event.target.value);
  };

  const handleDateFilter = () => {
    dispatch(
      setFilter({
        startDate: tempStartDate,
        endDate: tempEndDate,
      }),
    );
  };

  const handleClearFilters = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setTempStartDate("");
    setTempEndDate("");
    dispatch(resetAll());
  };

  const handleClearSearch = () => {
    dispatch(setSearch(""));
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const hasActiveFilters =
    filter?.keyword ||
    (filter?.filterType?.relatedBranch &&
      filter?.filterType?.relatedBranch !== "All") ||
    filter?.filterType?.relatedItem ||
    filter?.filterType?.relatedUnit ||
    filter?.filterType?.logType;

  const hasDateFilters =
    filter?.filterType?.startDate || filter?.filterType?.endDate;

  const productItemOptions = useMemo(
    () =>
      productItemData?.data?.map((item) => ({
        value: item._id,
        label: item.medicineItemName,
      })) || [],
    [productItemData],
  );

  const selectedProductItem = useMemo(
    () =>
      productItemOptions.find(
        (opt) => opt.value === filter.filterType?.relatedItem,
      ) || null,
    [filter.filterType?.relatedItem, productItemOptions],
  );

  const filterItemUnits = productItemData?.data?.find(
    (item) => item._id === filter.filterType?.relatedItem,
  )?.relatedItemUnits;

  useEffect(() => {
    if (branchData?.data?.length > 0) {
      dispatch(setFilter({ relatedBranch: branchData?.data[0]?._id }));
    }
  }, [branchData]);

  useEffect(() => {
    if (filter?.filterType?.relatedBranch) {
      fetchProductItem({
        isPopulateUnits: "true",
        relatedBranch: filter.filterType?.relatedBranch,
      });
    }
  }, [filter]);

  const handleResetFilters = () => {
    dispatch(resetAll());
  };

  return (
    <>
      {/* Main Search and Filters Card */}
      <Card sx={{ mb: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="subtitle2"
            color="primary"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Search & Filters
          </Typography>

          <Grid container spacing={2} alignItems="center">
            {/* Product Item Filter */}
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={productItemOptions}
                value={selectedProductItem}
                onChange={handleProductItemChange}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product Item"
                    placeholder="Search Product Item..."
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                )}
                size="small"
                noOptionsText="No product items found"
              />
            </Grid>

            {/* Item Unit Filter */}
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Unit</InputLabel>
                <Select
                  value={filter.filterType?.relatedUnit ?? ""}
                  onChange={handleUnitChange}
                  label="Unit"
                  sx={{ borderRadius: 2 }}
                  disabled={!filter.filterType?.relatedItem}
                >
                  {filterItemUnits?.map((unit) => (
                    <MenuItem key={unit._id} value={unit._id}>
                      {unit.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Branch Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={filter.filterType?.relatedBranch ?? "All"}
                  onChange={handleBranchChange}
                  label="Branch"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="All">All Branches</MenuItem>
                  {!branchLoading &&
                    branchData?.data?.map((branch) => (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Log Type Filter + Reset */}
            <Grid item xs={12} md={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Log Type</InputLabel>
                  <Select
                    value={filter?.filterType?.logType || ""}
                    onChange={handleLogTypeChange}
                    label="Log Type"
                    sx={{ borderRadius: 2 }}
                  >
                    {logTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip
                  title={
                    hasActiveFilters ? "Reset filters" : "No active filters"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleResetFilters}
                      disabled={!hasActiveFilters}
                      color="primary"
                      sx={{ borderRadius: 2, flexShrink: 0 }}
                    >
                      <RotateCcw size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>

          {/* Actions Row */}
          {/* <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box display="flex" gap={1} justifyContent="flex-end">
                {hasActiveFilters && (
                  <Tooltip title="Clear all filters">
                    <IconButton
                      onClick={handleClearFilters}
                      color="primary"
                  sx={{
                    borderRadius: 2,
                    "&.Mui-disabled": {
                      opacity: 0.5,
                      cursor: "default",
                    },
                  }}
                    >
                      <XIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid> */}

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <FilterList sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" color="textSecondary">
                Active filters:
              </Typography>

              {filter?.keyword && (
                <Chip
                  label={`Search: ${filter.keyword}`}
                  size="small"
                  onDelete={handleClearSearch}
                  color="primary"
                  variant="outlined"
                />
              )}

              {filter?.filterType?.relatedItem && (
                <Chip
                  label={`Item: ${productItemOptions.find((opt) => opt.value === filter.filterType.relatedItem)?.label || filter.filterType.relatedItem}`}
                  size="small"
                  onDelete={() =>
                    dispatch(
                      setFilter({ relatedItem: null, relatedUnit: "" }),
                    )
                  }
                  color="primary"
                  variant="outlined"
                />
              )}

              {filter?.filterType?.relatedUnit && (
                  <Chip
                    label={`Unit: ${filterItemUnits?.find((u) => u._id === filter.filterType.relatedUnit)?.name || filter.filterType.relatedUnit}`}
                    size="small"
                    onDelete={() => dispatch(setFilter({ relatedUnit: "" }))}
                    color="primary"
                    variant="outlined"
                  />
                )}

              {filter?.filterType?.relatedBranch &&
                filter?.filterType?.relatedBranch !== "All" && (
                  <Chip
                    label={`Branch: ${branchData?.data?.find((b) => b._id === filter.filterType.relatedBranch)?.name || filter.filterType.relatedBranch}`}
                    size="small"
                    onDelete={() => dispatch(setFilter({ relatedBranch: "" }))}
                    color="primary"
                    variant="outlined"
                  />
                )}

              {filter?.filterType?.logType && (
                <Chip
                  label={`Log: ${filter.filterType.logType}`}
                  size="small"
                  onDelete={() => dispatch(setFilter({ logType: "" }))}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Date Range Filter Card */}
      <Card sx={{ mb: 3, boxShadow: 1 }}>
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ fontWeight: 600 }}
            >
              Date Range Filter
            </Typography>
            <Box flexGrow={1} />

            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={tempStartDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                borderRadius: 2,
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              size="small"
              type="date"
              label="End Date"
              value={tempEndDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                borderRadius: 2,
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              variant="outlined"
              size="small"
              onClick={handleDateFilter}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Apply Filter
            </Button>
          </Box>

          {hasDateFilters && (
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <FilterList sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" color="textSecondary">
                Active filters:
              </Typography>

              {filter?.filterType?.startDate && (
                <Chip
                  label={`Start: ${filter.filterType.startDate}`}
                  size="small"
                  onDelete={() => dispatch(setFilter({ startDate: "" }))}
                  color="primary"
                  variant="outlined"
                />
              )}

              {filter?.filterType?.endDate && (
                <Chip
                  label={`End: ${filter.filterType.endDate}`}
                  size="small"
                  onDelete={() => dispatch(setFilter({ endDate: "" }))}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default StockHistoryHeader;
